import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import type { GuildPermission, GuildRoleResponse } from '@/lib/api';
import { DEFAULT_ROLE_COLOR, ROLE_DOT_FALLBACK_COLOR } from '../lib/role-colors';

/**
 * Mirrors `GUILD_PERMISSION_LABELS` in `@fias/db-types`. Duplicated as display
 * copy rather than imported because the client bundle doesn't depend on
 * db-types; the permission KEYS are what must stay in sync, and the server
 * rejects any key it doesn't recognise.
 */
const PERMISSION_LABELS: Record<GuildPermission, { label: string; description: string }> = {
  manage_guild: {
    label: 'Manage guild',
    description: 'Edit name, description, visibility, and settings.',
  },
  manage_roles: { label: 'Manage roles', description: 'Create roles and assign them to members.' },
  manage_channels: {
    label: 'Manage channels',
    description: 'Create, rename, and delete guild channels.',
  },
  manage_invites: { label: 'Manage invites', description: 'Create and revoke guild invites.' },
  kick_members: { label: 'Kick members', description: 'Remove members from the guild.' },
  ban_members: { label: 'Ban members', description: 'Ban and unban users.' },
  view_treasury: { label: 'View treasury', description: 'See the balance and transactions.' },
  manage_treasury: { label: 'Manage treasury', description: 'Move funds out of the treasury.' },
};

const PERMISSION_ORDER: GuildPermission[] = [
  'manage_guild',
  'manage_roles',
  'manage_channels',
  'manage_invites',
  'kick_members',
  'ban_members',
  'view_treasury',
  'manage_treasury',
];

interface GuildRolesManagerProps {
  roles: GuildRoleResponse[];
  loading: boolean;
  canManage: boolean;
  onCreate: (input: { name: string; color?: string | null }) => Promise<unknown>;
  onUpdatePermissions: (roleId: string, permissions: GuildPermission[]) => Promise<unknown>;
  onDelete: (roleId: string) => Promise<unknown>;
}

/**
 * Create custom roles and toggle what each one grants.
 *
 * Roles are ADDITIVE on top of a member's base role, so this matrix only ever
 * adds capability — unchecking everything leaves a member with exactly what
 * their base role already implied, never less. The copy says so, because
 * "Manage roles" unchecked looking like a revocation is the obvious misreading.
 */
export function GuildRolesManager({
  roles,
  loading,
  canManage,
  onCreate,
  onUpdatePermissions,
  onDelete,
}: GuildRolesManagerProps) {
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_ROLE_COLOR);
  const [creating, setCreating] = useState(false);
  const [busyRoleId, setBusyRoleId] = useState<string | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<GuildRoleResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      await onCreate({ name: newName.trim(), color: newColor });
      setNewName('');
      setNewColor(DEFAULT_ROLE_COLOR);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the role');
    } finally {
      setCreating(false);
    }
  };

  const togglePermission = async (
    role: GuildRoleResponse,
    permission: GuildPermission,
    checked: boolean,
  ) => {
    const next = checked
      ? [...role.permissions, permission]
      : role.permissions.filter((p) => p !== permission);
    setBusyRoleId(role.roleId);
    setError(null);
    try {
      await onUpdatePermissions(role.roleId, next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update the role');
    } finally {
      setBusyRoleId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!roleToDelete) return;
    setBusyRoleId(roleToDelete.roleId);
    setError(null);
    try {
      await onDelete(roleToDelete.roleId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the role');
    } finally {
      setBusyRoleId(null);
      setRoleToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Custom roles <strong>add</strong> permissions on top of a member’s base role (owner, admin,
        moderator, member). They never take one away, and an owner always keeps every permission.
      </p>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {canManage && (
        <form onSubmit={handleCreate} className="flex items-end gap-2 flex-wrap">
          <div className="space-y-2 flex-1 min-w-[12rem]">
            <Label htmlFor="new-role-name">New role</Label>
            <Input
              id="new-role-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Staff"
              maxLength={50}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-role-color">Colour</Label>
            <Input
              id="new-role-color"
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              className="w-16 p-1 h-10"
            />
          </div>
          <Button type="submit" disabled={creating || !newName.trim()}>
            {creating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Create role
          </Button>
        </form>
      )}

      {roles.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">
          No custom roles yet. Every member still has their base role.
        </p>
      ) : (
        <div className="space-y-4">
          {roles.map((role) => (
            <div key={role.roleId} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    aria-hidden="true"
                    className="h-3 w-3 rounded-full flex-shrink-0"
                    style={{ background: role.color ?? ROLE_DOT_FALLBACK_COLOR }}
                  />
                  <span className="font-medium truncate">{role.name}</span>
                  <span className="text-xs text-muted-foreground flex-shrink-0">
                    {role.memberCount ?? 0} {role.memberCount === 1 ? 'member' : 'members'}
                  </span>
                </div>
                {canManage && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`Delete ${role.name} role`}
                    disabled={busyRoleId === role.roleId}
                    onClick={() => setRoleToDelete(role)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                {PERMISSION_ORDER.map((permission) => {
                  const id = `${role.roleId}-${permission}`;
                  const checked = role.permissions.includes(permission);
                  return (
                    <div key={permission} className="flex items-start gap-2">
                      <Checkbox
                        id={id}
                        checked={checked}
                        disabled={!canManage || busyRoleId === role.roleId}
                        onCheckedChange={(value) =>
                          void togglePermission(role, permission, value === true)
                        }
                      />
                      <div className="min-w-0">
                        <Label htmlFor={id} className="text-sm font-normal cursor-pointer">
                          {PERMISSION_LABELS[permission].label}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {PERMISSION_LABELS[permission].description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog
        open={roleToDelete !== null}
        onOpenChange={(open) => {
          if (!open && busyRoleId === null) setRoleToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete the “{roleToDelete?.name}” role?</AlertDialogTitle>
            <AlertDialogDescription>
              Members holding it lose the permissions it grants. Their base role is unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busyRoleId !== null}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDeleteConfirm();
              }}
              disabled={busyRoleId !== null}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busyRoleId !== null ? 'Deleting…' : 'Delete role'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
